import React from "react";
import { useNavigate } from "react-router-dom";
import { ImagePath } from "../../assests";
import {
  FooterData,
  FooterServicesData,
  pageTypeOptions,
} from "../../utils/Customer/CustomerData";

interface CustomerFooterProps {
  showWave?: boolean;
  className?: string;
  setCurrentMode?: (
    mode: "Products" | "Pets" | "Services"
  ) => void;
}

const CustomerFooter: React.FC<CustomerFooterProps> = ({
  showWave = true,
  className = "",
  setCurrentMode,
}) => {
  const navigate = useNavigate();

  // ============================================================
  // CUSTOMER CARE LINKS
  // Opens in SAME TAB
  // ============================================================

  const handleLinkClick = (id: string) => {
    switch (id) {
      case "1":
        navigate("/careers");
        break;

      case "2":
        navigate("/blog");
        break;

      case "3":
        navigate("/contactus");
        break;

      case "4":
        navigate("/faq");
        break;

      case "5":
        navigate("/termsAndConditions");
        break;

      default:
        navigate("/");
        break;
    }
  };

  // ============================================================
  // FOOTER PAGE LINKS
  // Opens in SAME TAB
  // ============================================================

  const handlePageLinkClick = (id: string) => {
    switch (id) {
      case "1":
        navigate("/aboutUs");
        break;

      case "2":
        navigate("/privacyPolicy");
        break;

      case "3":
        navigate("/termsAndConditions");
        break;

      case "4":
        navigate("/returnAndExchange");
        break;

      case "5":
        navigate("/cancellationPolicy");
        break;

      case "6":
        navigate("/shippingPolicy");
        break;

      default:
        navigate("/");
        break;
    }

    // Always move page to top after navigation
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ============================================================
  // SERVICES
  // ============================================================

  const handleServicesClick = () => {
    setCurrentMode?.("Services");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative bg-accent">
      {/* ========================================================
          TOP WAVE
      ======================================================== */}

      {showWave && (
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
          <svg
            viewBox="0 0 1440 160"
            className="w-full h-24"
            preserveAspectRatio="none"
          >
            <path
              d="
                M0,100
                C180,150 360,60 540,110
                C720,150 900,60 1080,110
                C1260,150 1440,80 1440,80
                L1440,0
                L0,0
                Z
              "
              fill="#ffffff"
            />
          </svg>
        </div>
      )}

      {/* ========================================================
          CONTENT
      ======================================================== */}

      <div className="relative pb-4 flex flex-col md:flex-row items-center md:items-start gap-10">
        {/* ======================================================
            LEFT IMAGE
        ====================================================== */}

        <div className="hidden lg:flex lg:w-1/4 lg:justify-start">
          <img
            src={ImagePath.Dog}
            alt="dog"
            className="h-full min-h-[300px] w-auto object-cover"
          />
        </div>

        {/* ======================================================
            RIGHT CONTENT
        ====================================================== */}

        <div
          className={`px-3 lg:px-6 w-full lg:w-3/4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 text-center lg:text-left justify-items-center lg:justify-items-start ${
            className || "pt-24"
          }`}
        >
          {/* ====================================================
              CUSTOMER CARE
          ==================================================== */}

          <div>
            <h3 className="font-semibold mb-3 text-lg text-logoHeading">
              Customer care
            </h3>

            <ul className="space-y-1 text-sm text-gray-800">
              {FooterData.map((item, i) => (
                <li
                  key={i}
                  onClick={() =>
                    handleLinkClick(item.id)
                  }
                  className="
                    cursor-pointer
                    transition
                    duration-200
                    hover:text-green-700
                    hover:scale-105
                  "
                >
                  {item.value}
                </li>
              ))}
            </ul>
          </div>

          {/* ====================================================
              FOOTER LIST
          ==================================================== */}

          <div>
            <h3 className="font-semibold mb-3 text-lg text-logoHeading">
              Footer List
            </h3>

            <ul className="space-y-1 text-sm text-gray-800">
              {pageTypeOptions.map((item, i) => (
                <li
                  key={i}
                  onClick={() =>
                    handlePageLinkClick(item.id)
                  }
                  className="
                    cursor-pointer
                    transition
                    duration-200
                    hover:text-green-700
                    hover:scale-105
                  "
                >
                  {item.label}
                </li>
              ))}
            </ul>
          </div>

          {/* ====================================================
              SERVICES
          ==================================================== */}

          <div>
            <h3 className="font-semibold mb-3 text-lg text-logoHeading">
              Services
            </h3>

            <ul className="space-y-1 text-sm text-gray-800">
              {FooterServicesData.map((item, i) => (
                <li
                  key={i}
                  onClick={handleServicesClick}
                  className="
                    cursor-pointer
                    transition
                    duration-200
                    hover:text-green-700
                    hover:scale-105
                  "
                >
                  {item.value}
                </li>
              ))}
            </ul>
          </div>

          {/* ====================================================
              FOLLOW US
          ==================================================== */}

          <div>
            <h3 className="font-semibold mb-3 text-lg text-logoHeading">
              Follow Us
            </h3>

            <p className="text-xs text-gray-800 mb-3">
              Stay connected with us on social media for
              the latest updates, new arrivals, special
              offers, and more.
            </p>

            {/* ==================================================
                SOCIAL MEDIA
            ================================================== */}

            <div className="flex justify-center md:justify-start gap-4">
              {/* Instagram - external, keep new tab */}

              <a
                href="https://www.instagram.com/petland.dxb/?hl=en"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img
                  src={ImagePath.Instgram}
                  alt="instagram"
                  className="
                    w-5
                    h-5
                    cursor-pointer
                    transition
                    hover:scale-110
                  "
                />
              </a>

             

              <a
                href="https://www.youtube.com/@Petlandgroupofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <img
                  src={ImagePath.Youtube}
                  alt="youtube"
                  className="
                    w-5
                    h-5
                    cursor-pointer
                    transition
                    hover:scale-110
                  "
                />
              </a>

              {/* Facebook - external, keep new tab */}

              <a
                href="https://www.facebook.com/p/Petland-100083613397937/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img
                  src={ImagePath.Facebook}
                  alt="facebook"
                  className="
                    w-5
                    h-5
                    cursor-pointer
                    transition
                    hover:scale-110
                  "
                />
              </a>
            </div>

            {/* ==================================================
                LANGUAGE SELECTOR
            ================================================== */}

            <div className="mt-5 flex justify-center md:justify-start">
              <div
                id="google_translate_element"
                className="google-translate-container"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerFooter;